// DiscoverPage — the auto-guess proposal surface (frontend redesign v0.3).
//
// Per drafts/coframe_frontend_redesign_v0_3.md §§1-7 + §11 S4: Discover is
// the step between Scope (Columns) and Declare. It runs the deterministic
// inference core over the scoped column pool (the DiscoverColumnSpecs op)
// and renders ONE proposal per column — the always-deterministic guess at
// each column's structural declaration:
//
//   category (metric|dimension, §3) · anchor A (§4) · M (§5, always [])
//   · default reducer + NAA a_block (§6/§6.1) — each with a per-field
//   confidence + an evidence "why?" disclosure.
//
// Load-bearing invariant #1: these are PROPOSALS ONLY. Nothing here authors
// the asserted layer — Declare ratifies (or corrects) each row, and that
// human ratification is the only path that calls the declaration ops.

import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../api/client";
import type { LineageReportView, ProposalRow } from "../api/client";
import { useSession } from "./SessionContext";

export function DiscoverPage() {
  const { client, session } = useSession();

  const [rows, setRows] = useState<ProposalRow[] | null>(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cachedTables, setCachedTables] = useState<string[]>([]);
  // Which proposal rows have their "why?" evidence expanded.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // "From processing code" source (ADR-023 L2): a file path to extract code
  // lineage from, the reports we've extracted this session, and busy/error.
  const [codePath, setCodePath] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [reports, setReports] = useState<LineageReportView[]>([]);

  // Load any already-persisted proposals (a prior Discover run) on mount.
  const loadProposals = useCallback(async () => {
    if (session === null) return;
    setLoading(true);
    setError(null);
    try {
      const r = await client.getProposals(session.session_id);
      setRows(r.rows);
    } catch (e: unknown) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [client, session]);

  useEffect(() => {
    if (session === null) {
      setRows(null);
      return;
    }
    void loadProposals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.session_id]);

  // Run the DiscoverColumnSpecs op over the scoped pool, then take its
  // (already-flattened) rows straight from the result.
  async function runDiscover(opts: { refresh?: boolean } = {}) {
    if (session === null) return;
    setRunning(true);
    setError(null);
    setCachedTables([]);
    try {
      const r = await client.runOp(session.session_id, "DiscoverColumnSpecs", {
        inputs: {},
        kwargs: opts.refresh ? { refresh: true } : {},
      });
      if (!r.success || !r.result) {
        throw new Error(r.error ?? "DiscoverColumnSpecs failed");
      }
      const result = r.result as {
        rows: ProposalRow[];
        cached_tables: string[];
      };
      setRows(result.rows);
      setCachedTables(result.cached_tables ?? []);
    } catch (e: unknown) {
      setError(formatError(e));
    } finally {
      setRunning(false);
    }
  }

  // Extract code lineage from a processing file (ADR-023 L2 "From processing
  // code" source), then re-run Discover so the new code-affirmation priors
  // feed the proposals. The extracted report needs no backend — it's pure
  // static analysis cached on the session — but the subsequent Discover does.
  async function runExtractLineage() {
    if (session === null || codePath.trim() === "") return;
    setExtracting(true);
    setExtractError(null);
    try {
      const r = await client.extractLineage(session.session_id, codePath.trim());
      if (!r.success || !r.result) {
        throw new Error(r.error ?? "ExtractLineage failed");
      }
      const report = (r.result as { report: LineageReportView }).report;
      setReports((prev) => {
        // Replace any prior report for the same source file.
        const rest = prev.filter((p) => p.source_file !== report.source_file);
        return [...rest, report];
      });
      setCodePath("");
      // Re-run Discover so the freshly-extracted priors shape the proposals.
      await runDiscover();
    } catch (e: unknown) {
      setExtractError(formatError(e));
    } finally {
      setExtracting(false);
    }
  }

  // Group proposals by physical table, sorted; dimensions before metrics
  // within a table so the anchor-bearing rows read top-down.
  const byTable = useMemo(() => {
    if (rows === null) return [];
    const groups = new Map<string, ProposalRow[]>();
    for (const r of rows) {
      const arr = groups.get(r.table);
      if (arr) arr.push(r);
      else groups.set(r.table, [r]);
    }
    return Array.from(groups.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([table, rs]) => ({
        table,
        rows: rs.slice().sort(sortRows),
      }));
  }, [rows]);

  if (session === null) {
    return (
      <section>
        <h1>Discover</h1>
        <p style={{ color: "#888" }}>
          Start a Lattice on the <em>Data Source</em> tab to run Discover.
        </p>
      </section>
    );
  }

  const poolSize = session.column_pool_size;
  const scopeEmpty = session.scope_tables.length === 0;
  const metricCount = rows?.filter((r) => r.category === "metric").length ?? 0;
  const dimCount = (rows?.length ?? 0) - metricCount;
  const codeCount =
    rows?.filter((r) => r.source === "code-affirmation").length ?? 0;

  return (
    <section>
      <header style={headerStyle}>
        <div>
          <h1 style={{ margin: 0 }}>Discover</h1>
          <p style={subheaderStyle}>
            The engine proposes a draft column-spec for every pooled column —
            category, anchor, reducer, NAA. These are{" "}
            <strong>proposals only</strong>; you ratify or correct each one on{" "}
            <em>Declare</em>.
          </p>
        </div>
        <div style={statsStyle}>
          <Stat label="in pool" value={String(poolSize)} />
          <Stat label="dimensions" value={String(dimCount)} />
          <Stat label="metrics" value={String(metricCount)} />
          {codeCount > 0 && (
            <Stat label="code-affirmed" value={String(codeCount)} />
          )}
        </div>
      </header>

      {error && (
        <div style={errorBlockStyle}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {scopeEmpty && (
        <div style={infoBlockStyle}>
          No tables in scope yet. Pick tables on the <strong>Data Source</strong>{" "}
          tab and pool columns on <strong>Columns</strong>, then run Discover.
        </div>
      )}
      {!scopeEmpty && poolSize === 0 && (
        <div style={infoBlockStyle}>
          No columns pooled yet. Select the columns this Lattice covers on the{" "}
          <strong>Columns</strong> tab — Discover runs over the scoped column
          pool.
        </div>
      )}

      <div style={controlsRowStyle}>
        <button
          onClick={() => void runDiscover()}
          style={running || scopeEmpty ? primaryButtonDisabledStyle : primaryButtonStyle}
          disabled={running || scopeEmpty}
          title="Run the inference core over the scoped column pool"
        >
          {running ? "Discovering…" : rows && rows.length > 0 ? "Re-run Discover" : "Run Discover"}
        </button>
        <button
          onClick={() => void runDiscover({ refresh: true })}
          style={ghostButtonStyle}
          disabled={running || scopeEmpty}
          title="Force re-profile + re-discover (ignores the session cache)"
        >
          Refresh
        </button>
        {cachedTables.length > 0 && (
          <span style={cacheNoteStyle}>
            served from cache: <code>{cachedTables.join(", ")}</code>
          </span>
        )}
      </div>

      <div style={codePanelStyle}>
        <div style={codePanelHeaderStyle}>
          <span style={codePanelTitleStyle}>+ Add processing code</span>
          <span style={codePanelHintStyle}>
            Point at an ETL/processing file (SQL or Python) to extract its
            grain &amp; reducers — these become{" "}
            <strong>code-affirmation</strong> priors that precede the data pass
            (ADR-023).
          </span>
        </div>
        <div style={codePanelInputRowStyle}>
          <input
            type="text"
            value={codePath}
            onChange={(e) => setCodePath(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void runExtractLineage();
            }}
            placeholder="path/to/build_table.sql"
            style={codeInputStyle}
            disabled={extracting}
          />
          <button
            onClick={() => void runExtractLineage()}
            style={
              extracting || codePath.trim() === ""
                ? codeButtonDisabledStyle
                : codeButtonStyle
            }
            disabled={extracting || codePath.trim() === ""}
            title="Statically extract lineage, then re-run Discover with the new priors"
          >
            {extracting ? "Extracting…" : "+ Add code"}
          </button>
        </div>
        {extractError && (
          <div style={codeErrorStyle}>
            <strong>Extract failed:</strong> {extractError}
          </div>
        )}
        {reports.length > 0 && (
          <ul style={reportListStyle}>
            {reports.map((rep) => (
              <li key={rep.source_file} style={reportItemStyle}>
                <code style={{ color: "#5c2780" }}>{rep.source_file}</code>
                {rep.output_table && (
                  <>
                    {" → "}
                    <code>{rep.output_table}</code>
                  </>
                )}
                <span style={reportMetaStyle}>
                  {rep.grain && rep.grain.columns.length > 0
                    ? ` · grain: ${rep.grain.columns.join(", ")}`
                    : ""}
                  {rep.measures.length > 0
                    ? ` · ${rep.measures.length} measure${rep.measures.length === 1 ? "" : "s"}`
                    : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {loading && <p style={{ color: "#888" }}>Loading proposals…</p>}

      {!loading && rows !== null && rows.length === 0 && !running && (
        <p style={{ color: "#888", marginTop: "1rem" }}>
          No proposals yet — run Discover to generate them.
        </p>
      )}

      {byTable.map(({ table, rows: rs }) => (
        <TableSection
          key={table}
          table={table}
          rows={rs}
          expanded={expanded}
          onToggle={(key) =>
            setExpanded((prev) => {
              const next = new Set(prev);
              if (next.has(key)) next.delete(key);
              else next.add(key);
              return next;
            })
          }
        />
      ))}
    </section>
  );
}

// ── Per-table section ─────────────────────────────────────────────────

function TableSection({
  table,
  rows,
  expanded,
  onToggle,
}: {
  table: string;
  rows: ProposalRow[];
  expanded: Set<string>;
  onToggle: (key: string) => void;
}) {
  return (
    <div style={tableSectionStyle}>
      <div style={tableSectionHeaderStyle}>
        <code style={tableSectionNameStyle}>{table}</code>
        <span style={tableSectionMetaStyle}>
          {rows.length} {rows.length === 1 ? "column" : "columns"}
        </span>
      </div>
      <table style={gridStyle}>
        <thead>
          <tr>
            <th style={thStyle}>column</th>
            <th style={thStyle}>type</th>
            <th style={thStyle}>category</th>
            <th style={thStyle}>anchor A</th>
            <th style={thStyle}>anchor M</th>
            <th style={thStyle}>reducer</th>
            <th style={thStyle}>NAA blocks</th>
            <th style={thStyle}>why?</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const key = `${r.table}|${r.column}`;
            const isOpen = expanded.has(key);
            const overall = overallConfidence(r.confidence);
            return (
              <Fragment key={key}>
                <tr style={trStyle}>
                  <td style={tdStyle}>
                    <code style={{ fontWeight: 500 }}>{r.column}</code>
                    {r.source === "code-affirmation" && (
                      <span
                        style={codeSourceBadgeStyle}
                        title="Shaped by a code-affirmation prior extracted from processing code (ADR-023)"
                      >
                        code
                      </span>
                    )}
                  </td>
                  <td style={tdMutedStyle}>{r.data_type}</td>
                  <td style={tdStyle}>
                    <span style={categoryBadgeStyle(r.category)}>
                      {r.category}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <AnchorCell A={r.A} name={r.column} />
                  </td>
                  <td style={tdStyle}>
                    <MAnchorCell M={r.M} />
                  </td>
                  <td style={tdStyle}>
                    {r.reducer_operator ? (
                      <code>{r.reducer_operator}</code>
                    ) : (
                      <span style={{ color: "#bbb" }}>—</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {r.reducer_a_block.length > 0 ? (
                      <span style={naaBlockStyle}>
                        {r.reducer_a_block.join(", ")}
                      </span>
                    ) : r.reducer_operator ? (
                      <span style={{ color: "#1a6b3a" }} title="Additive over every family">
                        none
                      </span>
                    ) : (
                      <span style={{ color: "#bbb" }}>—</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => onToggle(key)}
                      style={whyButtonStyle}
                      title="Show the per-field confidence + evidence"
                    >
                      <span style={confidenceDotStyle(overall)} />
                      {isOpen ? "hide" : "why?"}
                    </button>
                  </td>
                </tr>
                {isOpen && (
                  <tr key={`${key}-why`}>
                    <td colSpan={8} style={evidenceCellStyle}>
                      <EvidenceTable
                        confidence={r.confidence}
                        evidence={r.evidence}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Anchor V (the value anchor A) cell. A grain-key column anchors on its own
// name (self-by-name; ADR-024 — the eliminated "self" sentinel). We detect
// that as A === [columnName] and badge it as a grain key; everything else
// shows its anchor dims verbatim.
function AnchorCell({ A, name }: { A: string[]; name: string }) {
  if (A.length === 1 && A[0] === name) {
    return (
      <span style={selfAnchorStyle} title="Grain key — anchors on its own identity (self-by-name)">
        {name} · grain
      </span>
    );
  }
  if (A.length === 0) {
    return <span style={{ color: "#bbb" }}>—</span>;
  }
  return (
    <span style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
      {A.join(" · ")}
    </span>
  );
}

// Anchor M (MVD, Manual §5) cell. Under ADR-024 grain-key columns carry an
// EMPTY M — grain presence is a build-time invariant (D-005), not runtime
// MNAR — so they read "—" like any MCAR column. MNAR ⟺ name ∈ M, surfaced
// here as the explicit M anchor list. There is no longer a "self" sentinel.
function MAnchorCell({ M }: { M: string[] | null }) {
  if (M !== null && M.length > 0) {
    return (
      <span style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
        {M.join(" · ")}
      </span>
    );
  }
  return <span style={{ color: "#bbb" }}>—</span>;
}

// The per-field confidence + evidence disclosure (invariant-#1 "why?").
function EvidenceTable({
  confidence,
  evidence,
}: {
  confidence: Record<string, string>;
  evidence: Record<string, string>;
}) {
  const fields = Array.from(
    new Set([...Object.keys(confidence), ...Object.keys(evidence)]),
  ).sort();
  if (fields.length === 0) {
    return <span style={{ color: "#888" }}>No evidence recorded.</span>;
  }
  return (
    <table style={evidenceGridStyle}>
      <tbody>
        {fields.map((f) => {
          const c = confidence[f];
          return (
            <tr key={f}>
              <td style={evidenceFieldStyle}>{f}</td>
              <td style={evidenceConfStyle}>
                {c ? (
                  <span style={confBadgeStyle(c)}>{c}</span>
                ) : (
                  <span style={{ color: "#bbb" }}>—</span>
                )}
              </td>
              <td style={evidenceWhyStyle}>{evidence[f] ?? ""}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────

// Sort within a table: dimensions first, then metrics; alpha within each.
function sortRows(a: ProposalRow, b: ProposalRow): number {
  if (a.category !== b.category) return a.category === "dimension" ? -1 : 1;
  return a.column.localeCompare(b.column);
}

// Collapse the per-field confidences to a single worst-case badge for the
// "why?" dot (low < medium < high; untestable is its own worst-case).
function overallConfidence(confidence: Record<string, string>): string {
  const order: Record<string, number> = {
    untestable: 0,
    low: 1,
    medium: 2,
    high: 3,
  };
  let worst: string | null = null;
  for (const v of Object.values(confidence)) {
    if (worst === null || (order[v] ?? 1) < (order[worst] ?? 1)) worst = v;
  }
  return worst ?? "medium";
}

function formatError(e: unknown): string {
  if (e instanceof ApiError) return `${e.status} ${e.message}`;
  if (e instanceof Error) return e.message;
  return String(e);
}

// ── Small components ──────────────────────────────────────────────────

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={statStyle}>
      <div style={statLabelStyle}>{label}</div>
      <div style={statValueStyle}>{value}</div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "1.5rem",
  marginBottom: "1rem",
};
const subheaderStyle: React.CSSProperties = {
  color: "#666",
  margin: "0.25rem 0 0 0",
  fontSize: "0.9rem",
  maxWidth: 640,
};
const statsStyle: React.CSSProperties = {
  display: "flex",
  gap: "1.25rem",
  paddingTop: "0.3rem",
};
const statStyle: React.CSSProperties = { display: "flex", flexDirection: "column" };
const statLabelStyle: React.CSSProperties = {
  fontSize: "0.7rem",
  color: "#888",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
};
const statValueStyle: React.CSSProperties = { fontSize: "0.95rem", fontWeight: 500 };
const controlsRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "0.5rem",
  alignItems: "center",
  margin: "1rem 0",
};
const cacheNoteStyle: React.CSSProperties = {
  fontSize: "0.8rem",
  color: "#888",
};
const primaryButtonStyle: React.CSSProperties = {
  padding: "0.45rem 1rem",
  fontSize: "0.9rem",
  background: "#0366d6",
  color: "#fff",
  border: "1px solid #0366d6",
  borderRadius: 4,
  cursor: "pointer",
  fontWeight: 500,
};
const primaryButtonDisabledStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  background: "#e0e0e0",
  borderColor: "#cbd5e0",
  color: "#888",
  cursor: "not-allowed",
};
const ghostButtonStyle: React.CSSProperties = {
  padding: "0.4rem 0.7rem",
  fontSize: "0.85rem",
  background: "#fff",
  color: "#444",
  border: "1px solid #cbd5e0",
  borderRadius: 4,
  cursor: "pointer",
};
const codePanelStyle: React.CSSProperties = {
  border: "1px solid #c9a8e0",
  borderLeft: "4px solid #8338b0",
  background: "#faf7fd",
  borderRadius: 6,
  padding: "0.85rem 1rem",
  margin: "0 0 1.25rem 0",
};
const codeButtonStyle: React.CSSProperties = {
  padding: "0.4rem 0.9rem",
  fontSize: "0.85rem",
  background: "#8338b0",
  color: "#fff",
  border: "1px solid #8338b0",
  borderRadius: 4,
  cursor: "pointer",
  fontWeight: 600,
  whiteSpace: "nowrap",
};
const codeButtonDisabledStyle: React.CSSProperties = {
  ...codeButtonStyle,
  background: "#e7daf2",
  borderColor: "#d8c0e9",
  color: "#a78bbf",
  cursor: "not-allowed",
};
const codePanelHeaderStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.15rem",
  marginBottom: "0.5rem",
};
const codePanelTitleStyle: React.CSSProperties = {
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "#5c2780",
};
const codePanelHintStyle: React.CSSProperties = {
  fontSize: "0.8rem",
  color: "#6b5a7a",
};
const codePanelInputRowStyle: React.CSSProperties = {
  display: "flex",
  gap: "0.5rem",
  alignItems: "center",
};
const codeInputStyle: React.CSSProperties = {
  flex: 1,
  padding: "0.4rem 0.6rem",
  fontSize: "0.85rem",
  fontFamily: "monospace",
  border: "1px solid #cbd5e0",
  borderRadius: 4,
};
const codeErrorStyle: React.CSSProperties = {
  marginTop: "0.5rem",
  padding: "0.4rem 0.6rem",
  background: "#ffeef0",
  border: "1px solid #f3c0c0",
  borderRadius: 4,
  fontSize: "0.82rem",
};
const reportListStyle: React.CSSProperties = {
  listStyle: "none",
  margin: "0.6rem 0 0 0",
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: "0.25rem",
};
const reportItemStyle: React.CSSProperties = {
  fontSize: "0.82rem",
  color: "#444",
};
const reportMetaStyle: React.CSSProperties = {
  color: "#6b5a7a",
};
const codeSourceBadgeStyle: React.CSSProperties = {
  display: "inline-block",
  marginLeft: "0.4rem",
  padding: "0.02rem 0.35rem",
  borderRadius: 3,
  fontSize: "0.65rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  background: "#f4ecf9",
  color: "#5c2780",
  border: "1px solid #d8c0e9",
  verticalAlign: "middle",
};
const errorBlockStyle: React.CSSProperties = {
  padding: "0.6rem 0.8rem",
  background: "#ffeef0",
  border: "1px solid #f3c0c0",
  borderRadius: 4,
  marginBottom: "1rem",
  fontSize: "0.9rem",
};
const infoBlockStyle: React.CSSProperties = {
  padding: "0.75rem 1rem",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 6,
  color: "#444",
  fontSize: "0.9rem",
  marginBottom: "1rem",
};
const tableSectionStyle: React.CSSProperties = {
  marginBottom: "1.5rem",
  border: "1px solid #e2e8f0",
  borderRadius: 6,
  overflow: "hidden",
};
const tableSectionHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: "0.75rem",
  padding: "0.5rem 0.75rem",
  background: "#f1f5f9",
  borderBottom: "1px solid #e2e8f0",
};
const tableSectionNameStyle: React.CSSProperties = {
  fontSize: "0.95rem",
  fontWeight: 600,
  color: "#1c4e80",
};
const tableSectionMetaStyle: React.CSSProperties = {
  fontSize: "0.78rem",
  color: "#64748b",
  fontVariantNumeric: "tabular-nums",
};
const gridStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "0.9rem",
};
const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "0.45rem 0.6rem",
  background: "#fafafa",
  borderBottom: "2px solid #ddd",
  fontSize: "0.8rem",
  color: "#666",
  fontWeight: 500,
};
const trStyle: React.CSSProperties = {};
const tdStyle: React.CSSProperties = {
  padding: "0.4rem 0.6rem",
  borderBottom: "1px solid #eee",
  verticalAlign: "top",
};
const tdMutedStyle: React.CSSProperties = {
  ...tdStyle,
  color: "#888",
  fontFamily: "monospace",
  fontSize: "0.82rem",
};
const selfAnchorStyle: React.CSSProperties = {
  display: "inline-block",
  padding: "0.1rem 0.4rem",
  borderRadius: 3,
  fontSize: "0.75rem",
  background: "#f4ecf9",
  color: "#5c2780",
  border: "1px solid #d8c0e9",
};
const naaBlockStyle: React.CSSProperties = {
  display: "inline-block",
  padding: "0.1rem 0.4rem",
  borderRadius: 3,
  fontSize: "0.75rem",
  background: "#fff0f0",
  color: "#a02020",
  border: "1px solid #f3c0c0",
};
const whyButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.35rem",
  padding: "0.15rem 0.5rem",
  fontSize: "0.78rem",
  background: "#fff",
  color: "#444",
  border: "1px solid #cbd5e0",
  borderRadius: 3,
  cursor: "pointer",
};
const evidenceCellStyle: React.CSSProperties = {
  padding: "0.5rem 0.75rem 0.75rem 0.75rem",
  background: "#fafbfc",
  borderBottom: "1px solid #eee",
};
const evidenceGridStyle: React.CSSProperties = {
  borderCollapse: "collapse",
  fontSize: "0.83rem",
  width: "100%",
};
const evidenceFieldStyle: React.CSSProperties = {
  padding: "0.2rem 0.6rem 0.2rem 0",
  fontWeight: 600,
  color: "#555",
  whiteSpace: "nowrap",
  verticalAlign: "top",
  width: "1%",
};
const evidenceConfStyle: React.CSSProperties = {
  padding: "0.2rem 0.6rem 0.2rem 0",
  verticalAlign: "top",
  width: "1%",
};
const evidenceWhyStyle: React.CSSProperties = {
  padding: "0.2rem 0",
  color: "#444",
};

function categoryBadgeStyle(category: string): React.CSSProperties {
  const palette: Record<string, [string, string, string]> = {
    dimension: ["#e3edf9", "#1c4e80", "#bcd5f0"],
    metric: ["#e9f5e9", "#1a6b3a", "#bce0c1"],
  };
  const [bg, fg, border] = palette[category] ?? ["#f0f0f0", "#555", "#ddd"];
  return {
    background: bg,
    color: fg,
    border: `1px solid ${border}`,
    padding: "0.1rem 0.45rem",
    borderRadius: 3,
    fontSize: "0.78rem",
    fontWeight: 500,
  };
}

function confColors(conf: string): [string, string, string] {
  const palette: Record<string, [string, string, string]> = {
    high: ["#e9f5e9", "#1a6b3a", "#bce0c1"],
    medium: ["#fef2d6", "#8b6914", "#e6d8a8"],
    low: ["#fdeede", "#b5651d", "#f0d2ad"],
    untestable: ["#f0eaf6", "#6a3d99", "#d8c0e9"],
  };
  return palette[conf] ?? ["#f0f0f0", "#555", "#ddd"];
}

function confBadgeStyle(conf: string): React.CSSProperties {
  const [bg, fg, border] = confColors(conf);
  return {
    background: bg,
    color: fg,
    border: `1px solid ${border}`,
    padding: "0.05rem 0.4rem",
    borderRadius: 3,
    fontSize: "0.72rem",
    fontWeight: 600,
  };
}

function confidenceDotStyle(conf: string): React.CSSProperties {
  const [, fg] = confColors(conf);
  return {
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: fg,
  };
}
